import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Activity, AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import type { components } from '@/lib/generated/api-types'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/loading-spinner'
import {
  useCacheConfig,
  useCacheStatus,
  useForceRefresh,
  useUpdateCacheConfig,
} from '@/lib/api'
import { localizeDigits } from '@/lib/localize-digits'

type CacheConfigUpdate = components['schemas']['CacheConfigUpdate']

export const Route = createFileRoute('/settings/cache')({
  component: CacheMonitorPage,
})

function ModeBadge({ mode }: { mode: string }) {
  const { t } = useTranslation()
  const variants: Record<string, { className: string; label: string }> = {
    active: {
      className:
        'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30',
      label: t('cache.status.modeActive'),
    },
    idle: {
      className:
        'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30',
      label: t('cache.status.modeIdle'),
    },
    keep_warm: {
      className:
        'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30',
      label: t('cache.status.modeKeepWarm'),
    },
  }
  const v = variants[mode] ?? variants.idle
  return (
    <Badge variant="outline" className={v.className}>
      {v.label}
    </Badge>
  )
}

function formatUptime(seconds: number | null | undefined): string {
  if (seconds == null) return '-'
  if (seconds < 60) return `${Math.floor(seconds)}s`
  if (seconds < 3600)
    return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}m`
}

function CacheMonitorPage() {
  const { t } = useTranslation()
  const {
    data: status,
    isLoading: statusLoading,
    error: statusError,
  } = useCacheStatus(true)
  const { data: config } = useCacheConfig()
  const updateConfig = useUpdateCacheConfig()
  const forceRefresh = useForceRefresh()

  const [configForm, setConfigForm] = useState<CacheConfigUpdate>({})
  const [formInitialized, setFormInitialized] = useState(false)

  // Initialize form from config when loaded
  if (config && !formInitialized) {
    setConfigForm({
      idle_interval_ms: config.idle_interval_ms,
      active_interval_ms: config.active_interval_ms,
      keep_warm_duration_s: config.keep_warm_duration_s,
      ip_degraded_threshold: config.ip_degraded_threshold,
      max_per_ip_rpm: config.max_per_ip_rpm,
    })
    setFormInitialized(true)
  }

  const handleSaveConfig = async () => {
    try {
      await updateConfig.mutateAsync(configForm)
      toast.success(t('cache.config.saved'))
    } catch {
      toast.error(t('cache.config.error'))
    }
  }

  const handleForceRefresh = async () => {
    try {
      await forceRefresh.mutateAsync()
      toast.success(t('cache.config.refreshed'))
    } catch {
      toast.error(t('cache.config.refreshError'))
    }
  }

  const tracker = (status?.tracker_summary ?? {}) as Record<string, unknown>

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/settings">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold">{t('cache.title')}</h1>
                <p className="text-xs text-muted-foreground">
                  {t('cache.description')}
                </p>
              </div>
            </div>
            {status && <ModeBadge mode={status.mode} />}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {/* Loading */}
          {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
          {statusLoading && !status && (
            <Card>
              <CardContent className="py-12">
                <LoadingSpinner label={t('cache.status.loading')} />
              </CardContent>
            </Card>
          )}

          {/* Error */}
          {statusError && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3 text-center">
                  <Activity className="h-8 w-8 text-destructive" />
                  <p className="font-medium text-destructive">
                    {t('cache.status.error')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Degraded Warning */}
          {status?.is_degraded && (
            <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm dark:border-orange-800 dark:bg-orange-950">
              <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
              <span className="text-orange-800 dark:text-orange-200">
                {t('cache.status.degradedWarning')}
              </span>
            </div>
          )}

          {/* Card 1 — Status Overview */}
          {status && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('cache.status.title')}
                </CardTitle>
                <CardDescription>
                  {t('cache.status.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('cache.status.vehicleCount')}
                    </p>
                    <p className="font-medium">
                      {localizeDigits(status.vehicle_count)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('cache.status.consumers')}
                    </p>
                    <p className="font-medium">
                      {localizeDigits(status.active_consumer_count)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('cache.status.wsSubscribers')}
                    </p>
                    <p className="font-medium">
                      {localizeDigits(status.ws_subscriber_count)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('cache.status.uptime')}
                    </p>
                    <p className="font-medium">
                      {localizeDigits(formatUptime(status.uptime_seconds))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('cache.status.lastUpdate')}
                    </p>
                    <p className="font-medium text-xs">
                      {status.last_update
                        ? localizeDigits(
                            new Date(status.last_update).toLocaleTimeString(),
                          )
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('cache.status.effectiveInterval')}
                    </p>
                    <p className="font-medium">
                      {localizeDigits(`${status.effective_interval_ms}ms`)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {t('cache.status.availableIps')}
                    </p>
                    <p className="font-medium">
                      {localizeDigits(status.available_ips)}
                    </p>
                  </div>
                  {status.last_error && (
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">
                        {t('cache.status.lastError')}
                      </p>
                      <p className="font-medium text-xs text-destructive truncate">
                        {status.last_error}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card 2 — Tracker Summary */}
          {status && Object.keys(tracker).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('cache.tracker.title')}
                </CardTitle>
                <CardDescription>
                  {t('cache.tracker.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  {tracker.total_fetches != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t('cache.tracker.totalFetches')}
                      </p>
                      <p className="font-medium">
                        {localizeDigits(tracker.total_fetches as number)}
                      </p>
                    </div>
                  )}
                  {tracker.hash_changes != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t('cache.tracker.hashChanges')}
                      </p>
                      <p className="font-medium">
                        {localizeDigits(tracker.hash_changes as number)}
                      </p>
                    </div>
                  )}
                  {tracker.unchanged_count != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t('cache.tracker.unchanged')}
                      </p>
                      <p className="font-medium">
                        {localizeDigits(tracker.unchanged_count as number)}
                      </p>
                    </div>
                  )}
                  {tracker.avg_time_between_changes != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t('cache.tracker.avgTimeBetweenChanges')}
                      </p>
                      <p className="font-medium">
                        {localizeDigits(
                          `${Math.round(tracker.avg_time_between_changes as number)}s`,
                        )}
                      </p>
                    </div>
                  )}
                  {tracker.min_time_between_changes != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t('cache.tracker.minTimeBetweenChanges')}
                      </p>
                      <p className="font-medium">
                        {localizeDigits(
                          `${Math.round(tracker.min_time_between_changes as number)}s`,
                        )}
                      </p>
                    </div>
                  )}
                  {tracker.max_time_between_changes != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t('cache.tracker.maxTimeBetweenChanges')}
                      </p>
                      <p className="font-medium">
                        {localizeDigits(
                          `${Math.round(tracker.max_time_between_changes as number)}s`,
                        )}
                      </p>
                    </div>
                  )}
                  {tracker.communauto_cache_hits != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t('cache.tracker.cacheHits')}
                      </p>
                      <p className="font-medium">
                        {localizeDigits(
                          tracker.communauto_cache_hits as number,
                        )}
                      </p>
                    </div>
                  )}
                  {tracker.communauto_fresh_count != null && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {t('cache.tracker.freshCount')}
                      </p>
                      <p className="font-medium">
                        {localizeDigits(
                          tracker.communauto_fresh_count as number,
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card 3 — Configuration */}
          {formInitialized && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t('cache.config.title')}
                </CardTitle>
                <CardDescription>
                  {t('cache.config.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="idle-interval">
                    {t('cache.config.idleInterval')}
                  </Label>
                  <Input
                    id="idle-interval"
                    type="number"
                    min={1000}
                    max={30000}
                    value={configForm.idle_interval_ms ?? ''}
                    onChange={(e) =>
                      setConfigForm((f) => ({
                        ...f,
                        idle_interval_ms: Number(e.target.value),
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">1000–30000 ms</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="active-interval">
                    {t('cache.config.activeInterval')}
                  </Label>
                  <Input
                    id="active-interval"
                    type="number"
                    min={100}
                    max={5000}
                    value={configForm.active_interval_ms ?? ''}
                    onChange={(e) =>
                      setConfigForm((f) => ({
                        ...f,
                        active_interval_ms: Number(e.target.value),
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">100–5000 ms</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="keep-warm">
                    {t('cache.config.keepWarmDuration')}
                  </Label>
                  <Input
                    id="keep-warm"
                    type="number"
                    min={0}
                    max={600}
                    value={configForm.keep_warm_duration_s ?? ''}
                    onChange={(e) =>
                      setConfigForm((f) => ({
                        ...f,
                        keep_warm_duration_s: Number(e.target.value),
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">0–600 s</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ip-threshold">
                    {t('cache.config.ipDegradedThreshold')}
                  </Label>
                  <Input
                    id="ip-threshold"
                    type="number"
                    min={1}
                    max={64}
                    value={configForm.ip_degraded_threshold ?? ''}
                    onChange={(e) =>
                      setConfigForm((f) => ({
                        ...f,
                        ip_degraded_threshold: Number(e.target.value),
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">1–64</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-rpm">
                    {t('cache.config.maxPerIpRpm')}
                  </Label>
                  <Input
                    id="max-rpm"
                    type="number"
                    min={1}
                    max={30}
                    value={configForm.max_per_ip_rpm ?? ''}
                    onChange={(e) =>
                      setConfigForm((f) => ({
                        ...f,
                        max_per_ip_rpm: Number(e.target.value),
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">1–30</p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleSaveConfig}
                    disabled={updateConfig.isPending}
                    className="flex-1"
                    size="sm"
                  >
                    {updateConfig.isPending ? (
                      <RefreshCw className="h-4 w-4 me-2 animate-spin" />
                    ) : null}
                    {t('cache.config.save')}
                  </Button>
                  <Button
                    onClick={handleForceRefresh}
                    disabled={forceRefresh.isPending}
                    variant="outline"
                    className="flex-1"
                    size="sm"
                  >
                    {forceRefresh.isPending ? (
                      <RefreshCw className="h-4 w-4 me-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 me-2" />
                    )}
                    {t('cache.config.forceRefresh')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
